import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import {
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@goaltree/ui/components/sidebar'
import { cn } from '@goaltree/ui/lib/utils'
import { useNavigate, useRouterState, useSearch } from '@tanstack/react-router'
import { useQuery } from 'convex/react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useUpcomingCount } from '@/components/events-agenda'
import {
  TODAY_TABS,
  TODAY_TAB_LABEL,
  type TodayTab,
  useTodayTabDots,
} from '@/components/today-tabs'
import { keyToDate, logicalDayStr } from '@/lib/day'
import { habitDueOn } from '@/lib/schedule'
import { useViewStore } from '@/store/view.store'

type SidebarTab = {
  key: string
  title: string
  active: boolean
  meta?: string | number
  dot?: boolean
  dotLabel?: string
  onSelect: () => void
}

function SidebarTabList({ tabs }: { tabs: SidebarTab[] }) {
  return (
    <SidebarMenuSub className="mt-0.5 mr-0 pr-0">
      {tabs.map((tab) => (
        <SidebarMenuSubItem key={tab.key}>
          <SidebarMenuSubButton
            isActive={tab.active}
            aria-current={tab.active ? 'page' : undefined}
            render={<button type="button" />}
            onClick={tab.onSelect}
            className="relative w-full cursor-pointer text-[12.5px] data-active:font-semibold data-active:before:absolute data-active:before:inset-y-1.5 data-active:before:left-0 data-active:before:w-[3px] data-active:before:rounded-r-full data-active:before:bg-primary"
          >
            <span className="min-w-0 truncate">{tab.title}</span>
            {tab.dot ? (
              <span
                role="status"
                aria-label={tab.dotLabel}
                className="ml-auto size-1.5 shrink-0 rounded-full bg-primary"
              />
            ) : (
              tab.meta !== undefined && (
                <span
                  className={cn(
                    'ml-auto shrink-0 text-[10px] font-medium tabular-nums',
                    tab.active ? 'text-foreground/70' : 'text-muted-foreground/60',
                  )}
                >
                  {tab.meta}
                </span>
              )
            )}
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      ))}
    </SidebarMenuSub>
  )
}

const useOnPage = (path: string) =>
  useRouterState({ select: (s) => s.location.pathname.replace(/\/$/, '') }) === path

const useSearchTab = () => useSearch({ strict: false, select: (s) => s.tab as string | undefined })

export function TodaySidebarTabs() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const raw = useSearchTab()
  const tab: TodayTab = raw === 'friends' || raw === 'groves' ? raw : 'today'
  const onPage = useOnPage('/today')
  const dots = useTodayTabDots(onPage ? tab : undefined)
  return (
    <SidebarTabList
      tabs={TODAY_TABS.map((tk) => ({
        key: tk,
        title: t(TODAY_TAB_LABEL[tk]),
        active: onPage && tk === tab,
        dot: dots[tk],
        dotLabel: t('today.tabNew'),
        onSelect: () =>
          void navigate({
            to: '/today',
            search: tk === 'today' ? {} : { tab: tk },
            replace: true,
            resetScroll: false,
          }),
      }))}
    />
  )
}

// Shared by the Everyday row and its sub-items so the numbers agree: the row shows
// what's left today (habits due but not done + open tasks).
export function useEverydayCounts(inboxId: Id<'nodes'> | undefined) {
  const tasks = useQuery(api.nodes.getByParent, inboxId ? { parentId: inboxId } : 'skip')
  const habits = useQuery(api.nodes.getHabits)
  return useMemo(() => {
    const today = logicalDayStr(Date.now())
    const due = (habits ?? []).filter((h) => !h.frozen && habitDueOn(h, keyToDate(today)))
    const habitsDone = due.filter((h) =>
      h.completions.some((ms) => logicalDayStr(ms) === today),
    ).length
    const openTasks = (tasks ?? []).filter((n) => !n.isHabit && n.completedAt === null).length
    return {
      habitsDue: due.length,
      habitsDone,
      openTasks,
      left: due.length - habitsDone + openTasks,
    }
  }, [habits, tasks])
}

export function EverydaySidebarTabs({ inboxId }: { inboxId: Id<'nodes'> | undefined }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const onPage = useOnPage('/everyday')
  const tab = useViewStore((s) => s.inboxTab)
  const setTab = useViewStore((s) => s.setInboxTab)
  const upcoming = useUpcomingCount()
  const { habitsDue, habitsDone, openTasks } = useEverydayCounts(inboxId)
  const tabs = [
    {
      key: 'habits',
      title: t('search.groupHabits'),
      meta: habitsDue ? `${habitsDone}/${habitsDue}` : undefined,
    },
    { key: 'tasks', title: t('inbox.tabTasks'), meta: openTasks || undefined },
    { key: 'events', title: t('upcoming.modeUpcoming'), meta: upcoming || undefined },
  ] as const
  return (
    <SidebarTabList
      tabs={tabs.map((x) => ({
        ...x,
        active: onPage && x.key === tab,
        onSelect: () => {
          setTab(x.key)
          if (!onPage) void navigate({ to: '/everyday' })
        },
      }))}
    />
  )
}

const EXPLORE_TABS = ['forYou', 'following', 'templates'] as const

export function ExploreSidebarTabs() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const raw = useSearchTab()
  const tab = raw === 'following' || raw === 'templates' ? raw : 'forYou'
  const onPage = useOnPage('/explore')
  return (
    <SidebarTabList
      tabs={EXPLORE_TABS.map((k) => ({
        key: k,
        title: t(`discover.tabs.${k}`),
        active: onPage && k === tab,
        onSelect: () =>
          void navigate({
            to: '/explore',
            search: k === 'forYou' ? {} : { tab: k },
            replace: true,
          }),
      }))}
    />
  )
}

const LEADERBOARD_TABS = [
  ['week', 'leaderboard.thisWeekTab'],
  ['streak', 'leaderboard.streakTab'],
  ['badges', 'leaderboard.badgesTab'],
] as const

export function LeaderboardSidebarTabs() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const raw = useSearchTab()
  const tab = raw === 'streak' || raw === 'badges' ? raw : 'week'
  const onPage = useOnPage('/leaderboard')
  return (
    <SidebarTabList
      tabs={LEADERBOARD_TABS.map(([k, label]) => ({
        key: k,
        title: t(label),
        active: onPage && k === tab,
        onSelect: () =>
          void navigate({
            to: '/leaderboard',
            search: k === 'week' ? {} : { tab: k },
            replace: true,
            resetScroll: false,
          }),
      }))}
    />
  )
}
